import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Hand, CheckCircle2, RotateCcw, Sparkles, Loader2, UserPlus, CalendarClock, Pencil, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_STYLES, STATUS_STYLES, fmtDueDate, isOverdue } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const FILTERS = ["all", "open", "in-progress", "done"];

function AssignPopover({ task, users, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(task.assigned_to_id || "");
  const [due, setDue] = useState(task.due_date ? String(task.due_date).slice(0, 10) : "");

  const save = () => {
    if (!selected || !due) return;
    onUpdate(task.id, { assigned_to_id: selected, due_date: due });
    setOpen(false);
  };
  const clear = () => {
    onUpdate(task.id, { assigned_to_id: "" });
    setSelected("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`assign-task-${task.id}`}
          className={`text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
            task.assigned_to_id
              ? "bg-violet-950/50 text-violet-300 border-violet-900/40 hover:bg-violet-900/50"
              : "bg-[#262936] text-gray-400 border-[#2E3242] hover:text-gray-200"
          }`}
        >
          <UserPlus className="w-3 h-3" /> {task.assigned_to_id ? "Reassign" : "Assign"}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 bg-slate-900/95 backdrop-blur-md border-red-900/30 text-gray-200 p-3"
        data-testid={`assign-popover-${task.id}`}
      >
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Assign to</p>
        <div className="max-h-40 overflow-y-auto space-y-1 mb-3">
          {users.map((u) => (
            <button
              key={u.id}
              data-testid={`assign-member-${task.id}-${u.id}`}
              onClick={() => setSelected(u.id)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                selected === u.id
                  ? "bg-red-950/60 border border-red-900/50 text-white"
                  : "hover:bg-[#262936] border border-transparent text-gray-300"
              }`}
            >
              <img src={u.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
              <span className="truncate">{u.name}</span>
            </button>
          ))}
        </div>
        <label className="text-[11px] uppercase tracking-wider text-gray-500">Due date *</label>
        <Input
          data-testid={`assign-due-${task.id}`}
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="mt-1 mb-3 bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-9 text-sm"
        />
        <div className="flex items-center gap-2">
          <Button
            data-testid={`assign-confirm-${task.id}`}
            onClick={save}
            disabled={!selected || !due}
            className="ruby-gradient text-white border border-red-900/40 h-8 flex-1 text-sm"
          >
            Assign
          </Button>
          {task.assigned_to_id && (
            <button
              data-testid={`assign-clear-${task.id}`}
              onClick={clear}
              className="text-xs text-gray-400 hover:text-red-300 px-2"
            >
              Clear
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function TaskBoard({ tasks, users = [], onCreate, onUpdate, onDelete, onAiAdd, variant = "desktop" }) {
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [title, setTitle] = useState("");
  const [aiMode, setAiMode] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const sfx = variant === "mobile" ? "-mobile" : "";

  const beginEdit = (t) => {
    setEditingId(t.id);
    setEditTitle(t.title);
  };
  const saveEdit = (t) => {
    const v = editTitle.trim();
    if (v && v !== t.title) onUpdate(t.id, { title: v });
    setEditingId(null);
  };

  const filtered = tasks.filter((t) => (filter === "all" ? true : t.status === filter));
  const counts = {
    open: tasks.filter((t) => t.status === "open").length,
    "in-progress": tasks.filter((t) => t.status === "in-progress").length,
    done: tasks.filter((t) => t.status === "done").length,
  };

  const add = async (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || aiBusy) return;
    if (aiMode && onAiAdd) {
      setAiBusy(true);
      try {
        await onAiAdd(t);
        setTitle("");
      } finally {
        setAiBusy(false);
      }
    } else {
      onCreate(t);
      setTitle("");
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#121318] w-full lg:w-[350px] border-l border-[#2E3242]">
      <div className="px-4 h-16 flex items-center justify-between border-b border-[#2E3242] shrink-0">
        <div>
          <h2 className="font-display font-bold text-sm text-gray-100">Task Board</h2>
          <p className="text-[11px] text-gray-500">Synced live · {tasks.length} tasks</p>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="text-amber-400">{counts.open} open</span>
          <span className="text-gray-600">·</span>
          <span className="text-blue-400">{counts["in-progress"]} active</span>
        </div>
      </div>

      <div className="flex gap-1 p-3 border-b border-[#2E3242] shrink-0">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`task-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs px-2.5 py-1.5 rounded-md capitalize transition-colors ${
              filter === f
                ? "bg-red-950/60 text-red-300 border border-red-900/50"
                : "text-gray-500 hover:text-gray-300 border border-transparent"
            }`}
          >
            {f === "in-progress" ? "active" : f}
          </button>
        ))}
      </div>

      <form onSubmit={add} className="p-3 border-b border-[#2E3242] shrink-0 space-y-2">
        <div className="flex gap-2">
          <button
            type="button"
            data-testid={`task-ai-toggle${sfx}`}
            onClick={() => setAiMode((v) => !v)}
            title={aiMode ? "AI mode on — describe what to add" : "Ask AI to add tasks"}
            className={`w-9 h-9 shrink-0 rounded-md flex items-center justify-center border transition-colors ${
              aiMode
                ? "ruby-gradient text-white border-red-900/40"
                : "bg-[#16181F] border-[#2E3242] text-gray-400 hover:text-red-300 hover:border-red-900/40"
            }`}
          >
            <Sparkles className="w-4 h-4" />
          </button>
          <Input
            data-testid={`new-task-input${sfx}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={aiMode ? "Tell AI what to add…" : "Add a task…"}
            disabled={aiBusy}
            className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-9 text-sm"
          />
          <Button
            data-testid={`add-task-button${sfx}`}
            type="submit"
            disabled={!title.trim() || aiBusy}
            className="ruby-gradient text-white border border-red-900/40 h-9 px-3"
          >
            {aiBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : aiMode ? (
              <Sparkles className="w-4 h-4" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </Button>
        </div>
        {aiMode && (
          <p className="text-[11px] text-red-300/80 flex items-center gap-1.5 px-0.5">
            <Sparkles className="w-3 h-3" /> AI will turn your note into trackable to-dos
          </p>
        )}
      </form>

      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="task-list">
        {filtered.length === 0 && (
          <p className="text-center text-sm text-gray-600 mt-8">No tasks here yet.</p>
        )}
        <AnimatePresence initial={false}>
          {filtered.map((t) => {
            const status = STATUS_STYLES[t.status];
            const mine = t.assignee_id === user?.id;
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                data-testid={`task-card-${t.id}`}
                className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-3 hover:border-red-900/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  {editingId === t.id ? (
                    <div className="flex-1 flex items-center gap-1.5">
                      <Input
                        data-testid={`edit-task-input-${t.id}`}
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                        className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-8 text-sm"
                      />
                      <button data-testid={`edit-task-save-${t.id}`} onClick={() => saveEdit(t)} className="text-emerald-400 hover:text-emerald-300 shrink-0">
                        <Check className="w-4 h-4" />
                      </button>
                      <button data-testid={`edit-task-cancel-${t.id}`} onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-300 shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <p className={`text-sm leading-snug flex-1 ${t.status === "done" ? "line-through text-gray-500" : "text-gray-200"}`}>
                      {t.title}
                    </p>
                  )}
                  {editingId !== t.id && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        data-testid={`edit-task-${t.id}`}
                        onClick={() => beginEdit(t)}
                        className="text-gray-600 hover:text-blue-400"
                        title="Rename"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`delete-task-${t.id}`}
                        onClick={() => onDelete(t.id)}
                        className="text-gray-600 hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${PRIORITY_STYLES[t.priority]}`}>
                    {t.priority}
                  </span>
                  <span className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/30 ${status.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                  </span>
                  {t.source === "ai" && (
                    <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-950/50 text-red-300 border border-red-900/40">
                      <Sparkles className="w-2.5 h-2.5" /> AI
                    </span>
                  )}
                  {t.assigned_to_name && (
                    <span
                      data-testid={`task-assigned-tag-${t.id}`}
                      className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-950/50 text-violet-300 border border-violet-900/40"
                    >
                      <UserPlus className="w-2.5 h-2.5" /> {t.assigned_to_name}
                    </span>
                  )}
                  {t.due_date && (
                    <span
                      data-testid={`task-due-tag-${t.id}`}
                      className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded border ${
                        isOverdue(t.due_date, t.status)
                          ? "bg-red-950/60 text-red-300 border-red-800/60"
                          : "bg-black/30 text-gray-400 border-[#2E3242]"
                      }`}
                    >
                      <CalendarClock className="w-2.5 h-2.5" />
                      {isOverdue(t.due_date, t.status) ? "Overdue " : "Due "}
                      {fmtDueDate(t.due_date)}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between mt-2.5">
                  <div className="min-h-[24px] flex items-center">
                    {t.assignee_name ? (
                      <div className="flex items-center gap-1.5">
                        <img src={t.assignee_avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                        <span className="text-[11px] text-gray-400">
                          {mine ? "You're on it" : `${t.assignee_name} is on it`}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-600">Unassigned</span>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {t.status !== "done" && (
                      <AssignPopover task={t} users={users} onUpdate={onUpdate} />
                    )}
                    {t.status !== "done" && !t.assignee_id && (
                      <button
                        data-testid={`claim-task-${t.id}`}
                        onClick={() => onUpdate(t.id, { claim: true })}
                        className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md bg-blue-950/50 text-blue-300 border border-blue-900/40 hover:bg-blue-900/50"
                      >
                        <Hand className="w-3 h-3" /> I'm on it
                      </button>
                    )}
                    {t.status !== "done" && (
                      <button
                        data-testid={`complete-task-${t.id}`}
                        onClick={() => onUpdate(t.id, { status: "done" })}
                        className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-950/50 text-emerald-300 border border-emerald-900/40 hover:bg-emerald-900/50"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Done
                      </button>
                    )}
                    {t.status === "done" && (
                      <button
                        data-testid={`reopen-task-${t.id}`}
                        onClick={() => onUpdate(t.id, { status: "open" })}
                        className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md bg-[#262936] text-gray-400 border border-[#2E3242] hover:text-gray-200"
                      >
                        <RotateCcw className="w-3 h-3" /> Reopen
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
